/*
 * Copyright (c) 2023 Helvia Technologies PC <contact@helvia.io>
 */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

// components imports
import { Container } from "@/components/UI/Container";
import { Col, Row, Tabs, TabsProps } from "antd";
import { CreditCardOutlined, DollarOutlined, FileTextOutlined, ScheduleOutlined } from "@ant-design/icons";
import { MyInvoices } from "@/components/Subscriptions/MyInvoices";
import { Payments } from "@/components/Subscriptions/Payments";
import { PaymentMethods } from "@/components/Subscriptions/PaymentMethods";
import { SubscriptionsList } from "@/components/Subscriptions/SubscriptionsList";
import { DataLoadingSkeleton } from "@/components/UI/DataLoadingSkeleton";

// misc imports
import { useWorkspacesList } from "@/api/workspaces";

export const Subscriptions = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [items, setItems] = useState<TabsProps["items"] & { forceRender?: boolean }>([]);

  const { data: workspaces, isLoading: isLoadingWorkspaces } = useWorkspacesList();

  const searchParams = new URLSearchParams(location.search);
  const currentTab = searchParams.get("tab") || "";

  useEffect(() => {
    // Available tabs
    const _items = [
      {
        key: "subscriptions-list",
        label: t("subscriptions_screen.tabs_titles.subscriptions_list"),
        children: <SubscriptionsList />,
        icon: <DollarOutlined />,
        // forceRender: true
      },
      {
        key: "my-invoices",
        label: t("subscriptions_screen.tabs_titles.my_invoices"),
        children: <MyInvoices />,
        icon: <FileTextOutlined />,
      },
      {
        key: "payments",
        label: t("subscriptions_screen.tabs_titles.payments"),
        children: <Payments />,
        icon: <ScheduleOutlined />,
      },
      {
        key: "payments_methods",
        label: t("subscriptions_screen.payment_methods.title"),
        children: <PaymentMethods />,
        icon: <CreditCardOutlined />,
      },
    ];
    // Map all available keys to set the URL params
    const itemsKeys = _items.map((i) => i.key);
    if (!itemsKeys.includes(currentTab)) {
      if (!(searchParams.get("tab") === itemsKeys[0])) {
        setTimeout(() => {
          changeTab(itemsKeys[0]);
        });
      }
    }
    // Update the state
    setItems(_items);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const changeTab = (key: string) => {
    const nextUrlSearchParams = new URLSearchParams();
    nextUrlSearchParams.set("tab", key);
    navigate(
      {
        pathname: location.pathname,
        search: nextUrlSearchParams.toString(),
      },
      { replace: true }
    );
  };

  if (!workspaces && isLoadingWorkspaces) {
    return <DataLoadingSkeleton />;
  }

  return (
    <Container>
      <Row className="w-100" justify={"center"}>
        <Col xs={24}>
          {/* TABS to handle single project navigation */}
          <Tabs activeKey={currentTab} onChange={changeTab} items={items} indicator={{ size: 0 }} />
          {/* ./TABS to handle single project navigation */}
        </Col>
      </Row>
    </Container>
  );
};
